import type { Catalog } from '$lib/map/types/catalog'
import type { Material } from '$lib/map/types/material'

/** First material of the selected catalog's preloaded defaults, or null. */
export function firstMaterialForCatalog(
	catalog: Catalog | null,
	materialDefaultsByCatalog: Record<string, Material[]>
): Material | null {
	if (!catalog?.id) return null
	return materialDefaultsByCatalog[catalog.id]?.[0] ?? null
}

/**
 * Material options for the selected catalog. Keeps a selected material visible even when it is not
 * part of the catalog defaults (prefill), so an existing lot's saved material always renders.
 */
export function materialOptionsForCatalog(
	catalog: Catalog | null,
	materialDefaultsByCatalog: Record<string, Material[]>,
	selectedMaterial: Material | null
): Material[] {
	if (!catalog?.id) return []
	const base = materialDefaultsByCatalog[catalog.id] ?? []
	if (selectedMaterial && !base.some((m) => m.id === selectedMaterial.id)) {
		return [...base, selectedMaterial]
	}
	return base
}

/**
 * Catalog-scoped material autocomplete fetch URL, or undefined while defaults are still loading
 * (empty map). Once the map has any keys, fetch is set for the selected catalog — including when
 * that catalog already has preloaded defaults (type-to-search beyond defaults).
 */
export function materialFetchUrl(
	catalog: Catalog | null,
	materialDefaultsByCatalog: Record<string, Material[]>
): string | undefined {
	if (!catalog?.id) return undefined
	// empty map = still loading; avoid mount-time fetch-mode flicker ("Tippe um zu suchen")
	if (Object.keys(materialDefaultsByCatalog).length === 0) return undefined

	return `/api/material/autocomplete/svelte?catalogId=${catalog.id}&qry=[query]`
}
